<script>
    import Computation from '@js/modules/computation';
    import I18n from '@js/modules/translations';
    import {rideMetricKeys} from '@js/modules/searchResults';

    import Icon from '@js/components/themes/Icon.svelte';
    import {RIDE_TYPE_ICON} from '@js/components/themes/icons';
    import distanceIcon from '@js/components/icons/distanceIcon';
    import elevationIcon from '@js/components/icons/elevationIcon';
    import timeIcon from '@js/components/icons/timeIcon';
    import speedIcon from '@js/components/icons/speedIcon';
    import loopIcon from '@js/components/icons/loopIcon';
    import heartIcon from '@js/components/icons/heartIcon';
    import mapClockIcon from '@js/components/icons/mapClockIcon';
    import visibilityOffIcon from '@js/components/icons/visibilityOffIcon';

    import FavoriteButton from '@js/components/ride/FavoriteButton.svelte';

    const {
        ride,
        isHighlighted = false,
        compact = false
    } = $props();

    const METRIC_ICON = {
        distance: distanceIcon,
        elevation: elevationIcon,
        duration: timeIcon,
        pace: speedIcon,
        speed: speedIcon
    };

    const metricValue = (key) => {
        switch (key) {
            case 'distance':
                return Computation.formatDistance(ride.distance, {smartUnit: true, kUnit: false, decimalLimit: 1});
            case 'elevation':
                return '+' + Computation.formatDistance(ride.positiveElevation, {smartUnit: false, kUnit: false, decimalLimit: 0});
            case 'duration':
                return ride.formattedDuration;
            case 'pace':
                return Computation.formatPace(Computation.speedToUnit(ride.speed, {}));
            case 'speed':
                return Computation.formatSpeed(ride.speed);
            default:
                return null;
        }
    };

    const metrics = $derived(rideMetricKeys(ride)
        .map((key) => ({key, value: metricValue(key)}))
        .filter((metric) => !!metric.value));

    // Photo, then the static map, then the activity icon when an image fails to load
    let failedPictures = $state(0);
    const pictures = $derived([ride.defaultPicture?.mini?.jpg || ride.defaultPicture?.jpg, ride.staticMapMini].filter(Boolean));
    const picture = $derived(pictures[failedPictures]);

    const placeName = $derived(ride.placesBreadcrumb?.map((place) => place.title)
        .slice(-2)
        .join(' · '));
</script>

<a class="group flex items-stretch bg-white border rounded-xl shadow-sm overflow-hidden transition hover:shadow-md focus-visible:outline-2 focus-visible:outline-primary-600"
   class:border-primary-600={isHighlighted}
   class:ring-2={isHighlighted}
   class:ring-primary-200={isHighlighted}
   class:border-gray-200={!isHighlighted}
   href={ride.link}>
    <div class="relative shrink-0 bg-gray-100"
         class:w-28={compact}
         class:w-32={!compact}
         class:sm:w-40={!compact}>
        {#if picture}
            <img class="absolute inset-0 w-full h-full object-cover"
                 src={picture}
                 alt=""
                 loading="lazy"
                 decoding="async"
                 onerror={() => failedPictures++}/>
        {:else}
            <span class="absolute inset-0 flex items-center justify-center text-primary-300">
                <Icon class="w-10 h-10"
                      icon={RIDE_TYPE_ICON[ride.rideType]}/>
            </span>
        {/if}

        {#if !compact}
            <div class="absolute top-2 left-2">
                <FavoriteButton rideId={ride.id}/>
            </div>
        {/if}
    </div>

    <div class="flex flex-col justify-between min-w-0 flex-1 gap-1.5 p-3">
        <div class="flex items-center flex-wrap gap-1.5">
            <span class="inline-flex items-center px-2 py-0.5 text-xs font-semibold text-primary-900 bg-primary-100 rounded-full">
                <Icon class="w-3.5 h-3.5 me-1"
                      icon={RIDE_TYPE_ICON[ride.rideType]}/>
                {ride.rideTypeTranslated}
            </span>

            {#if ride.level && ride.level !== 'level_undefined'}
                <span class="px-2 py-0.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-full">
                    {ride.levelTranslated}
                </span>
            {/if}

            {#if ride.roundTrip}
                <span class="inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-full"
                      title={I18n.t('js.search.index.card.loop')}>
                    <Icon class="w-3.5 h-3.5 me-1"
                          icon={loopIcon}/>
                    {I18n.t('js.search.index.card.loop')}
                </span>
            {/if}

            {#if ride.planned}
                <span class="inline-flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full"
                      title={I18n.t('js.ride.common.planned')}>
                    <Icon class="w-3.5 h-3.5 text-primary-800"
                          icon={mapClockIcon}/>
                    <span class="sr-only">{I18n.t('js.ride.common.planned')}</span>
                </span>
            {/if}

            {#if ride.visibility === 'only_me'}
                <span class="inline-flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full"
                      title={I18n.t('js.ride.enums.visibility.only_me')}>
                    <Icon class="w-3.5 h-3.5 text-primary-800"
                          icon={visibilityOffIcon}/>
                    <span class="sr-only">{I18n.t('js.ride.enums.visibility.only_me')}</span>
                </span>
            {/if}
        </div>

        <h3 class="m-0 text-base font-bold leading-snug text-gray-900 line-clamp-2 group-hover:text-primary-800">
            {ride.name}
        </h3>

        <ul class="flex items-center flex-wrap gap-x-3 gap-y-1 text-sm font-semibold text-gray-900">
            {#each metrics as metric (metric.key)}
                <li class="inline-flex items-center whitespace-nowrap">
                    <Icon class="w-4 h-4 me-1 text-gray-500"
                          icon={METRIC_ICON[metric.key]}/>
                    {metric.value}
                </li>
            {/each}
        </ul>

        {#if !compact && (placeName || ride.user || ride.bookmarksCount > 0)}
            <div class="flex items-center gap-1 min-w-0 text-xs text-gray-500">
                {#if placeName}
                    <span class="truncate">{placeName}</span>
                {/if}

                {#if ride.user?.pseudo}
                    <span class="truncate">{I18n.t('js.search.index.card.by', {user: ride.user.pseudo})}</span>
                {/if}

                {#if ride.bookmarksCount > 0}
                    <span class="inline-flex items-center shrink-0"
                          title={I18n.t('js.ride.favorites.social_proof', {count: ride.bookmarksCount})}>
                        <Icon class="w-3.5 h-3.5 me-0.5 text-red-500"
                              icon={heartIcon}/>
                        {ride.bookmarksCount}
                    </span>
                {/if}
            </div>
        {/if}
    </div>
</a>
